import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { deletePatient } from '../../../../services/patientFiles';
import type { ActionRunnerProps } from '../types';

/**
 * Admin-only soft-delete for a patient. Detaches the patient from its practice
 * (the patient is then filtered out of every listing) and best-effort deletes
 * the corresponding Monday item.
 *
 * The confirm step requires typing `DELETE` verbatim, case-sensitive. After a
 * fully successful delete the page navigates to home because the current
 * PatientFiles route is no longer valid (the patient is now hidden). If the
 * Monday cleanup fails, the page stays on a warning screen so the admin
 * notices and can clean up the orphan Monday item manually.
 */
function DeletePatientAction({ patientId, patientName, onBack, onDataChange }: ActionRunnerProps) {
    const navigate = useNavigate();
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [mondayCleanupFailed, setMondayCleanupFailed] = useState(false);

    const canDelete = confirmText.trim() === 'DELETE' && !isDeleting;

    const handleDelete = useCallback(async () => {
        if (!canDelete) return;
        setIsDeleting(true);
        setError(null);
        try {
            const result = await deletePatient(patientId);
            onDataChange?.();
            if (result.mondayDeleteSucceeded) {
                navigate('/', { replace: true });
            } else {
                // DB detach succeeded but Monday cleanup didn't — keep the admin on
                // this page with a warning so the orphan doesn't go unnoticed.
                setMondayCleanupFailed(true);
                setIsDeleting(false);
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to delete patient';
            setError(message);
            setIsDeleting(false);
        }
    }, [canDelete, patientId, onDataChange, navigate]);

    if (mondayCleanupFailed) {
        return (
            <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                    <div className="w-[120px]" />
                    <div className="text-sm font-semibold text-gray-800">Partial delete</div>
                    <div className="w-[120px]" />
                </div>
                <div className="flex-1 min-h-0 overflow-auto p-6">
                    <div className="max-w-xl mx-auto">
                        <div className="flex items-start gap-3 p-4 rounded-xl border border-yellow-300 bg-yellow-50">
                            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-yellow-900">
                                <div className="font-semibold mb-1">
                                    Patient hidden, but Monday cleanup failed.
                                </div>
                                <p>
                                    <span className="font-medium">{patientName}</span> has been
                                    detached from the practice and is no longer visible in our
                                    system. However, the corresponding Monday.com item could not be
                                    deleted automatically. Please remove it manually from the Monday
                                    board.
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => navigate('/', { replace: true })}
                                className="bg-gray-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-800 transition-colors"
                            >
                                I understand, take me to home
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={onBack}
                    className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                >
                    <ArrowLeftIcon className="w-3.5 h-3.5" />
                    Back to actions
                </button>
                <div className="text-sm font-semibold text-gray-800">Delete patient</div>
                <div className="w-[120px]" />
            </div>

            <div className="flex-1 min-h-0 overflow-auto p-6">
                <div className="max-w-xl mx-auto">
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-red-800">
                            <div className="font-semibold mb-1">This is a one-way operation.</div>
                            <p>
                                Once deleted, this patient cannot be restored. Make sure this is
                                what you want before proceeding.
                            </p>
                        </div>
                    </div>

                    <div className="mt-6 p-4 rounded-xl border border-gray-200 bg-gray-50">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            You are about to delete
                        </div>
                        <div className="text-base font-semibold text-gray-900">{patientName}</div>
                    </div>

                    <div className="mt-6">
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Type <span className="font-mono text-gray-900">DELETE</span> to confirm
                        </label>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="DELETE"
                            autoComplete="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                        />
                    </div>

                    {error && (
                        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end items-center gap-3">
                        <button
                            onClick={onBack}
                            disabled={isDeleting}
                            className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={!canDelete}
                            className="flex items-center gap-2 bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDeleting && (
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            )}
                            {isDeleting ? 'Deleting…' : 'Delete patient'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { DeletePatientAction };
